// Company credit adjustments for platform administrators (SEC-14): a small
// adjustment is applied at once; one at or above the dual-approval amount
// waits until a second platform administrator approves it. The requester can
// never approve their own request (the server answers 403).
import {
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Radio,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { useBrand } from "../../shared/brand/brand";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { useMe } from "../../shared/session/me";
import { parseMoney, useFormat } from "../../shared/ui/format";
import { FormError, Notice } from "../../shared/ui/Page";
import { AdminLayout } from "./AdminLayout";
import { CompanyPicker, useAdminCompanies } from "./CompanyPicker";

type Adjustment = Schemas["CreditAdjustment"];
type Status = Adjustment["status"];
type Company = Schemas["Company"];

const KEY = ["admin", "credit-adjustments"] as const;
const STATUS_COLOUR: Record<Status, string> = { pending: "yellow", applied: "green", rejected: "gray" };

export function AdminCreditPage() {
  const { t } = useTranslation();
  return (
    <AdminLayout title={t("admin.title")}>
      <Credit />
    </AdminLayout>
  );
}

function Credit() {
  const { t } = useTranslation();
  const f = useFormat();
  const { currency } = useBrand();
  const me = useMe();
  const queryClient = useQueryClient();
  const companies = useAdminCompanies(true);
  const [filter, setFilter] = useState<Status | "all">("pending");
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const adjustments = useQuery({
    queryKey: [...KEY, filter],
    queryFn: () =>
      unwrap(
        api.GET("/admin/credit-adjustments", {
          params: { query: { limit: 200, ...(filter === "all" ? {} : { status: filter }) } },
        }),
      ),
  });
  const dual = adjustments.data?.dual_approval_minor;
  const items = adjustments.data?.items ?? [];
  const nameOf = (id: string) =>
    companies.data?.items.find((c) => c.id === id)?.name ?? t("admin_credit.unknown_company");
  const amountOf = (a: Adjustment) => f.money(a.amount_minor, currency);

  const decide = async (a: Adjustment, decision: "approve" | "reject") => {
    setError(null);
    setNotice(null);
    const { data, error, response } = await api.POST("/admin/credit-adjustments/{adjustment_id}/decision", {
      params: { path: { adjustment_id: a.id } },
      body: { decision },
    });
    if (!data) {
      if (response.status === 403 && decision === "approve") return setError(t("admin_credit.self_approval"));
      return setError(errorMessage(error, t("error.generic")));
    }
    const vars = { amount: amountOf(data), name: nameOf(data.company_id) };
    setNotice(
      data.status === "applied" ? t("admin_credit.approved_notice", vars) : t("admin_credit.rejected_notice", vars),
    );
    void queryClient.invalidateQueries({ queryKey: KEY });
  };

  return (
    <Stack gap="md">
      {dual !== undefined && (
        <Text c="dimmed" data-testid="dual-approval">
          {t("admin_credit.intro", { amount: f.money(dual, currency) })}
        </Text>
      )}
      {notice && <Notice>{notice}</Notice>}
      <RequestAdjustment
        dual={dual}
        onRequested={(a, c) => {
          const vars = { amount: amountOf(a), name: c.name };
          setError(null);
          setNotice(a.status === "applied" ? t("admin_credit.applied", vars) : t("admin_credit.pending", vars));
          void queryClient.invalidateQueries({ queryKey: KEY });
        }}
      />
      <Title order={2} size="h4">
        {t("admin_credit.list_title")}
      </Title>
      <Group gap="sm">
        <Text size="sm" id="credit-filter">
          {t("admin_credit.filter")}
        </Text>
        <SegmentedControl
          size="xs"
          aria-labelledby="credit-filter"
          value={filter}
          onChange={(v) => setFilter(v as Status | "all")}
          data={[
            { value: "pending", label: t("admin_credit.status_pending") },
            { value: "applied", label: t("admin_credit.status_applied") },
            { value: "rejected", label: t("admin_credit.status_rejected") },
            { value: "all", label: t("admin_credit.all") },
          ]}
        />
      </Group>
      <FormError message={error} />
      {adjustments.isPending ? (
        <Loader size="sm" aria-label={t("common.loading")} />
      ) : adjustments.isError ? (
        <FormError message={errorMessage(adjustments.error, t("error.generic"))} />
      ) : items.length === 0 ? (
        <Text c="dimmed" data-testid="credit-empty">
          {t("admin_credit.empty")}
        </Text>
      ) : (
        <Table.ScrollContainer minWidth={760}>
          <Table striped verticalSpacing="sm" aria-label={t("admin_credit.list_title")}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("admin_credit.company")}</Table.Th>
                <Table.Th ta="right">{t("admin_credit.amount")}</Table.Th>
                <Table.Th>{t("admin_credit.reason")}</Table.Th>
                <Table.Th>{t("admin_credit.requested")}</Table.Th>
                <Table.Th>{t("admin_companies.status")}</Table.Th>
                <Table.Th>{t("admin_companies.actions")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((a) => {
                const mine = a.requested_by === me.data?.account_id;
                const vars = { amount: amountOf(a), name: nameOf(a.company_id) };
                return (
                  <Table.Tr key={a.id} data-testid="credit-row">
                    <Table.Td>{nameOf(a.company_id)}</Table.Td>
                    <Table.Td ta="right">{amountOf(a)}</Table.Td>
                    <Table.Td>
                      <Text size="sm" style={{ wordBreak: "break-word" }}>
                        {a.reason}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{f.dateTime(a.requested_at)}</Text>
                      <Text size="xs" c="dimmed" style={{ wordBreak: "break-all" }}>
                        {t("admin_credit.requested_by")}: {mine ? t("admin_credit.you") : a.requested_by}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={STATUS_COLOUR[a.status]} variant="light">
                        {t(`admin_credit.status_${a.status}`)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {a.status === "pending" && (
                        <Group gap={4} wrap="nowrap">
                          <Button
                            size="xs"
                            variant="light"
                            onClick={() => void decide(a, "approve")}
                            aria-label={t("admin_credit.approve_for", vars)}
                          >
                            {t("admin_credit.approve")}
                          </Button>
                          <Button
                            size="xs"
                            variant="subtle"
                            color="red"
                            onClick={() => void decide(a, "reject")}
                            aria-label={t("admin_credit.reject_for", vars)}
                          >
                            {t("admin_credit.reject")}
                          </Button>
                        </Group>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Stack>
  );
}

function RequestAdjustment({
  dual,
  onRequested,
}: {
  dual: number | undefined;
  onRequested: (a: Adjustment, c: Company) => void;
}) {
  const { t } = useTranslation();
  const { currency } = useBrand();
  const [company, setCompany] = useState<Company | null>(null);
  const [direction, setDirection] = useState<"add" | "remove">("add");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const minor = parseMoney(amount, currency);
  const needsSecond = dual !== undefined && minor !== null && minor >= dual;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!company) return setError(t("admin_payments.pick_company"));
    if (minor === null) return setError(t("admin_payments.bad_amount"));
    const r = reason.trim();
    if (r.length < 3 || r.length > 500) return setError(t("admin_credit.bad_reason"));
    setError(null);
    setBusy(true);
    const { data, error } = await api.POST("/admin/credit-adjustments", {
      body: { company_id: company.id, amount_minor: direction === "add" ? minor : -minor, reason: r },
    });
    setBusy(false);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    setAmount("");
    setReason("");
    onRequested(data, company);
    setCompany(null);
  };

  return (
    <Paper withBorder p="md" radius="md">
      <form onSubmit={submit} data-testid="credit-form">
        <Stack gap="sm">
          <Title order={2} size="h4">
            {t("admin_credit.request_title")}
          </Title>
          <CompanyPicker value={company?.id ?? ""} onChange={setCompany} />
          <Radio.Group
            label={t("admin_credit.direction")}
            value={direction}
            onChange={(v) => setDirection(v as "add" | "remove")}
          >
            <Group gap="md" mt={6}>
              <Radio value="add" label={t("admin_credit.add")} />
              <Radio value="remove" label={t("admin_credit.remove")} />
            </Group>
          </Radio.Group>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput
              label={t("admin_payments.amount", { currency })}
              description={t("admin_payments.amount_hint")}
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.currentTarget.value)}
              required
            />
            <TextInput
              label={t("admin_credit.reason")}
              description={t("admin_credit.reason_hint")}
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              required
              maxLength={500}
            />
          </SimpleGrid>
          {needsSecond && (
            <Text size="sm" c="orange" data-testid="needs-second">
              {t("admin_credit.needs_second")}
            </Text>
          )}
          <FormError message={error} />
          <Button type="submit" loading={busy} w="fit-content">
            {t("admin_credit.submit")}
          </Button>
        </Stack>
      </form>
    </Paper>
  );
}
